import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { AuthShell, CodeInput, PasswordInput } from "../components/AuthShell";
import { useTranslation } from "../i18n";
import { Icon } from "../components/icons";
import { Alert, Button, Field, Input } from "../components/ui";
import { api, ApiError } from "../lib/api";

type Step = "email" | "code" | "success";
const MIN_PASSWORD_LENGTH = 8;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleRequestCode(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/password-reset/request", {
        method: "POST",
        body: { email },
        anonymous: true,
      });
      setStep("code");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("reset.requestFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/password-reset/confirm", {
        method: "POST",
        body: {
          email,
          code,
          newPassword,
          ...(requires2FA ? { totpCode } : {}),
        },
        anonymous: true,
      });
      setStep("success");
    } catch (cause) {
      if (cause instanceof ApiError) {
        if (cause.message.includes("Two-factor authentication code required")) {
          setRequires2FA(true);
          setError(t("reset.twoFactorRequired"));
        } else {
          setError(cause.message);
        }
      } else {
        setError(t("reset.resetFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (step === "success") {
    return (
      <AuthShell
        title={t("reset.success.title")}
        subtitle={t("reset.success.subtitle")}
        announce
        footer={
          <Link
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            to="/login"
            state={location.state}
          >
            {t("reset.success.signIn")}
          </Link>
        }
      >
        <div className="space-y-4 text-center">
          {/* Square, and from the icon set. This was a `rounded-full` accent
              circle around a hand-inlined checkmark path — a glyph the set
              already carries, drawn in a second style. */}
          <span className="mx-auto grid size-10 place-items-center rounded-md border border-mint/25 bg-mint/8 text-mint">
            <Icon name="check" className="size-4" />
          </span>
          <Button
            onClick={() => navigate("/login", { state: location.state })}
            size="xl"
            className="w-full"
          >
            {t("reset.success.continue")}
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (step === "code") {
    return (
      <AuthShell
        title={t("reset.code.title")}
        subtitle={t("reset.code.subtitle", { minutes: 15 })}
        footer={
          <button
            type="button"
            className="text-sm text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
            onClick={() => {
              setStep("email");
              setCode("");
              setNewPassword("");
              setTotpCode("");
              setRequires2FA(false);
              setUseRecoveryCode(false);
              setError(null);
            }}
          >
            {t("reset.code.differentEmail")}
          </button>
        }
      >
        <form onSubmit={handleResetPassword} className="space-y-5">
          {error && <Alert>{error}</Alert>}

          <div className="border-b border-line pb-4 text-center">
            <p className="micro text-accent">{t("reset.code.request")}</p>
            <p className="mt-2 break-all text-sm font-medium text-fg">{email}</p>
            <p className="mt-1 text-xs leading-5 text-fg-muted">
              {t("reset.code.mayArrive")}
            </p>
          </div>

          {/* The same field the two-factor challenge uses. Digit filtering and
              the six-character cap live in it now, not here. */}
          <CodeInput id="code" label={t("reset.code.resetCode")} value={code} onChange={setCode} autoFocus />

          <Field label={t("reset.code.newPassword")} htmlFor="newPassword" hint={t("reset.code.passwordHint", { min: MIN_PASSWORD_LENGTH })}>
            <PasswordInput
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              minLength={MIN_PASSWORD_LENGTH}
            />
          </Field>

          {requires2FA && (
            <div className="space-y-3 border-t border-line pt-4">
              <div>
                <p className="text-[13px] font-medium text-fg">{t("reset.code.twoFactor")}</p>
                <p className="mt-0.5 text-[13px] leading-5 text-fg-muted">
                  {t("reset.code.twoFactorBody")}
                </p>
              </div>
              <CodeInput
                id="totpCode"
                label={useRecoveryCode ? t("verify.recoveryCode") : t("verify.authCode")}
                value={totpCode}
                onChange={setTotpCode}
                numeric={!useRecoveryCode}
              />
              <button
                type="button"
                onClick={() => {
                  setUseRecoveryCode((current) => !current);
                  setTotpCode("");
                }}
                className="text-left text-[13px] text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
              >
                {useRecoveryCode ? t("verify.useAuthenticator") : t("verify.useRecovery")}
              </button>
            </div>
          )}

          <Button type="submit" size="xl" loading={submitting} className="w-full">
            {t("reset.code.submit")}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t("reset.email.title")}
      subtitle={t("reset.email.subtitle")}
      footer={
        <>
          {t("reset.email.remember")}{" "}
          <Link
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            to="/login"
            state={location.state}
          >
            {t("reset.email.signIn")}
          </Link>
        </>
      }
    >
      <form onSubmit={handleRequestCode} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Field label={t("reset.email.email")} htmlFor="email">
          <Input
            id="email"
            size="xl"
            surface="canvas"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Button type="submit" size="xl" loading={submitting} className="w-full">
          {t("reset.email.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
